import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import equipmentActions from "../../../../_actions/equipmentActions";
import ContractorSelector from "../_common/ContractorSelector";
import EquipmentCatalogBrowserModal from "../../../_common/EquipmentCatalogBrowserModal";
import { BASE_URL } from "../../../../_constants/urlConstants";

const CATALOG_BASE = `${BASE_URL}/api/ProjectEquipment/Catalog`;
const MODE = { MANUAL: "manual", STANDARD: "standard", MISC: "misc" };

const MODE_SLOT_KEY = {
  [MODE.STANDARD]: "standardSelection",
  [MODE.MISC]: "miscSelection",
  [MODE.MANUAL]: "manualEntry",
};

const BLANK_CATALOG_SELECTION = {
  selectedClass: null, equipmentClass: "", equipmentClassDescription: "",
  selectedMake: null, equipmentMake: "", equipmentMakeDescription: "",
  selectedModel: null, equipmentModel: "", equipmentModelDescription: "",
};

function buildCatalogYearOptions(referenceDate) {
  const d = referenceDate ? new Date(referenceDate) : new Date();
  const fyStartYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return Array.from({ length: 6 }, (_, i) => {
    const y = fyStartYear - (5 - i);
    return {
      value: `${y}-04-01`,
      label: `${String(y).slice(2)}/${String(y + 1).slice(2)}`,
    };
  });
}

const norm = (s) => (s || "").trim().toUpperCase();

// "" -> null, "0" -> 0 (unlike `parseFloat(v) || null`, which would wrongly
// turn a legitimate zero rate into null since 0 is falsy).
const parseRate = (v) => {
  const trimmed = (v || "").toString().trim();
  return trimmed === "" ? null : parseFloat(trimmed);
};

class EditEquipmentModal extends Component {
  constructor(props) {
    super(props);
    const catalogYearOptions = buildCatalogYearOptions(null);
    this.state = {
      entryMode: MODE.STANDARD,
      selectedCatalogYear: catalogYearOptions[catalogYearOptions.length - 1],
      catalogYearOptions,
      formData: {
        projectEquipmentId: 0,
        projectId: 0,
        contractorId: 0,
        equipmentNumber: "",
        isRental: false,
        isManualEntry: true,
        equipmentType: "E",
        description: "",
      },
      // Each entry mode keeps its own independent Class/Make/Code (+ Rate for
      // Manual) so switching modes never discards what was entered elsewhere —
      // it's a pure display swap, not a shared-then-reconciled field.
      standardSelection: { ...BLANK_CATALOG_SELECTION },
      miscSelection: { ...BLANK_CATALOG_SELECTION },
      manualEntry: {
        equipmentClass: "", equipmentMake: "", equipmentModel: "",
        rate: "", delayFactor: "", otFactor: "",
      },
      catalogClasses: [],
      catalogMakes: [],
      catalogModels: [],
      catalogRate: null,
      loadingCatalog: false,
      reconciling: false,
      search: { query: "", results: [], loading: false },
      originalNumber: "",
      originalClass: "",
      originalMake: "",
      originalModel: "",
      impactData: null,
      checkingImpact: false,
      showCatalogBrowser: false,
    };
    this._searchTimer = null;
  }

  get reportDateStr() {
    return this.state.selectedCatalogYear
      ? this.state.selectedCatalogYear.value
      : new Date().toISOString().split("T")[0];
  }

  get equipmentType() {
    return this.state.entryMode === MODE.STANDARD ? "E" : "M";
  }

  // ─── Active-slot helpers ─────────────────────────────────────────────────
  // Standard/Misc/Manual each have their own Class/Make/Code slot; these
  // resolve "the slot for whichever mode is currently active" so every
  // handler below can stay mode-agnostic.

  getActiveSelection = () => this.state[MODE_SLOT_KEY[this.state.entryMode]];

  setActiveSelection = (partial, extraState, callback) => {
    const key = MODE_SLOT_KEY[this.state.entryMode];
    this.setState((s) => ({ [key]: { ...s[key], ...partial }, ...extraState }), callback);
  };

  componentDidMount() {
    const { equipmentDetails, isNew, projectId } = this.props;
    if (!isNew && equipmentDetails) {
      let mode = MODE.MANUAL;
      if (!equipmentDetails.isManualEntry) {
        mode = equipmentDetails.equipmentType === "M" ? MODE.MISC : MODE.STANDARD;
      }

      // Build the react-select value objects directly from the equipment's own stored
      // class/make/model so they display correctly even if the catalog lookup below can't
      // find a match for the default catalog year (e.g. legacy equipment recorded under a
      // prior rate period). Opening the modal never clears/discards existing data.
      const catalogSlot = {
        selectedClass: equipmentDetails.equipmentClass
          ? {
              value: equipmentDetails.equipmentClass,
              label: `[ ${equipmentDetails.equipmentClass} ] ${equipmentDetails.equipmentClassDescription || ""}`,
              description: equipmentDetails.equipmentClassDescription,
            }
          : null,
        equipmentClass: equipmentDetails.equipmentClass || "",
        equipmentClassDescription: equipmentDetails.equipmentClassDescription || "",
        selectedMake: equipmentDetails.equipmentMake
          ? {
              value: equipmentDetails.equipmentMake,
              label: `[ ${equipmentDetails.equipmentMake} ] ${equipmentDetails.equipmentMakeDescription || ""}`,
              description: equipmentDetails.equipmentMakeDescription,
            }
          : null,
        equipmentMake: equipmentDetails.equipmentMake || "",
        equipmentMakeDescription: equipmentDetails.equipmentMakeDescription || "",
        selectedModel: equipmentDetails.equipmentModel
          ? {
              value: equipmentDetails.equipmentModel,
              label: `[ ${equipmentDetails.equipmentModel} ] ${equipmentDetails.equipmentModelDescription || ""}`,
              description: equipmentDetails.equipmentModelDescription,
            }
          : null,
        equipmentModel: equipmentDetails.equipmentModel || "",
        equipmentModelDescription: equipmentDetails.equipmentModelDescription || "",
      };
      const manualSlot = {
        equipmentClass: equipmentDetails.equipmentClass || "",
        equipmentMake: equipmentDetails.equipmentMake || "",
        equipmentModel: equipmentDetails.equipmentModel || "",
        rate: equipmentDetails.rate != null ? String(equipmentDetails.rate) : "",
        delayFactor: equipmentDetails.delayFactor != null ? String(equipmentDetails.delayFactor) : "",
        otFactor: equipmentDetails.otFactor != null ? String(equipmentDetails.otFactor) : "",
      };

      this.setState({
        entryMode: mode,
        originalNumber: equipmentDetails.number || "",
        originalClass: equipmentDetails.equipmentClass || "",
        originalMake: equipmentDetails.equipmentMake || "",
        originalModel: equipmentDetails.equipmentModel || "",
        formData: {
          ...this.state.formData,
          projectId,
          projectEquipmentId: equipmentDetails.projectEquipmentId,
          contractorId: equipmentDetails.contractorId,
          equipmentNumber: equipmentDetails.number || "",
          isRental: equipmentDetails.isRental || false,
          isManualEntry: equipmentDetails.isManualEntry,
          equipmentType: equipmentDetails.equipmentType || "E",
          description: equipmentDetails.description || "",
        },
        // Only the slot matching this record's actual saved mode gets populated —
        // the other two start blank, since there's no history for a mode this
        // record was never saved under.
        standardSelection: mode === MODE.STANDARD ? catalogSlot : { ...BLANK_CATALOG_SELECTION },
        miscSelection: mode === MODE.MISC ? catalogSlot : { ...BLANK_CATALOG_SELECTION },
        manualEntry: mode === MODE.MANUAL
          ? manualSlot
          : { equipmentClass: "", equipmentMake: "", equipmentModel: "", rate: "", delayFactor: "", otFactor: "" },
      }, () => { if (mode !== MODE.MANUAL) this.loadCatalogOptionsForSlot(catalogSlot); });
    } else if (isNew) {
      this.setState(
        { formData: { ...this.state.formData, projectId } },
        () => this.loadClasses("E")
      );
    }
  }

  // Populates the Class/Make/Code dropdown options and the rate panel for display.
  // Never clears the slot — if the stored values don't resolve against the default
  // catalog year, they simply won't appear selected in the (now-populated) dropdown
  // until the user picks the correct Catalog Year, or re-selects.
  loadCatalogOptionsForSlot = async (slot) => {
    await this.loadClasses(this.equipmentType);
    if (!slot.equipmentClass || !slot.equipmentMake) return;

    await this.loadMakes(slot.equipmentClass);
    const models = await this.loadModels(slot.equipmentClass, slot.equipmentMake);

    // Legacy equipment saved before ModelDescription was persisted may have a blank/stale
    // value on the stored record — the initial selectedModel label built in componentDidMount
    // is a placeholder in that case. Once the real catalog Model list loads, correct the
    // label (and the flat equipmentModelDescription field sent on save) from the matched entry.
    if (slot.equipmentModel) {
      const modelMatch = models.find((m) => norm(m.value) === norm(slot.equipmentModel));
      if (modelMatch) {
        this.setActiveSelection({
          selectedModel: { value: modelMatch.value, label: modelMatch.label, description: modelMatch.description },
          equipmentModelDescription: modelMatch.description,
        });
      }
      await this.loadRate(slot.equipmentClass, slot.equipmentMake, slot.equipmentModel);
    }
  };

  // ─── Catalog loaders (each returns the fetched list so callers can use it
  // synchronously without waiting on a second render) ─────────────────────────

  loadClasses = async (equipmentType) => {
    this.setState({ loadingCatalog: true });
    try {
      const res = await axios.get(`${CATALOG_BASE}/Classes`, {
        params: { equipmentType, reportDate: this.reportDateStr },
      });
      const classes = (res.data || []).map((c) => ({
        value: c.equipmentClass,
        label: `[ ${c.equipmentClass} ] ${c.equipmentClassDescription}`,
        description: c.equipmentClassDescription,
      }));
      this.setState({ catalogClasses: classes, loadingCatalog: false });
      return classes;
    } catch {
      this.setState({ catalogClasses: [], loadingCatalog: false });
      return [];
    }
  };

  loadMakes = async (equipmentClass) => {
    this.setState({ loadingCatalog: true });
    try {
      const res = await axios.get(`${CATALOG_BASE}/Makes`, {
        params: { equipmentClass, equipmentType: this.equipmentType, reportDate: this.reportDateStr },
      });
      const makes = (res.data || []).map((m) => ({
        value: m.equipmentMake,
        label: `[ ${m.equipmentMake} ] ${m.equipmentMakeDescription}`,
        description: m.equipmentMakeDescription,
      }));
      this.setState({ catalogMakes: makes, loadingCatalog: false });
      return makes;
    } catch {
      this.setState({ catalogMakes: [], loadingCatalog: false });
      return [];
    }
  };

  loadModels = async (equipmentClass, equipmentMake) => {
    this.setState({ loadingCatalog: true });
    try {
      const res = await axios.get(`${CATALOG_BASE}/Models`, {
        params: { equipmentClass, equipmentMake, equipmentType: this.equipmentType, reportDate: this.reportDateStr },
      });
      const models = (res.data || []).map((m) => ({
        value: m.equipmentModel,
        label: `[ ${m.equipmentModel} ] ${m.equipmentModelDescription}`,
        description: m.equipmentModelDescription,
      }));
      this.setState({ catalogModels: models, loadingCatalog: false });
      // No model codes for this make — fetch rate directly at class+make level
      if (models.length === 0) {
        this.loadRate(equipmentClass, equipmentMake, null);
      }
      return models;
    } catch {
      this.setState({ catalogModels: [], loadingCatalog: false });
      return [];
    }
  };

  loadRate = async (equipmentClass, equipmentMake, equipmentModel) => {
    try {
      const res = await axios.get(`${CATALOG_BASE}/Rate`, {
        params: {
          equipmentClass, equipmentMake, equipmentModel,
          equipmentType: this.equipmentType, reportDate: this.reportDateStr,
        },
      });
      this.setState({ catalogRate: res.data || null });
    } catch {
      this.setState({ catalogRate: null });
    }
  };

  // ─── Reconciliation on mode/year switch ────────────────────────────────────
  //
  // A <Select> can only ever show a valid catalog option or nothing, so whenever
  // the active mode's slot might now be stale (its own catalog year changed, or
  // it's being viewed for the first time), its stored Class/Make/Model is
  // checked against the current catalog and cleared — cascading down — the
  // moment it no longer matches. This never touches the *other* two slots.

  reconcileCatalogSelection = async () => {
    // Captured so a slower-to-resolve reconciliation from a mode/year the user has since
    // switched away from can detect it's stale and bail instead of writing its match into
    // whatever mode happens to be active by the time its awaited fetch comes back.
    const reconcileMode = this.state.entryMode;
    const stillCurrent = () => this.state.entryMode === reconcileMode;

    const slot = this.getActiveSelection(); // snapshot, same pattern as today
    const equipmentType = this.equipmentType;

    this.setState({ reconciling: true });

    const classes = await this.loadClasses(equipmentType);
    if (!stillCurrent()) return;
    const classMatch = slot.equipmentClass
      ? classes.find((c) => norm(c.value) === norm(slot.equipmentClass))
      : null;

    if (!classMatch) {
      this.setActiveSelection({ ...BLANK_CATALOG_SELECTION },
        { reconciling: false, catalogMakes: [], catalogModels: [], catalogRate: null });
      return;
    }

    this.setActiveSelection({
      selectedClass: { value: classMatch.value, label: classMatch.label, description: classMatch.description },
      equipmentClass: classMatch.value,
      equipmentClassDescription: classMatch.description,
    });

    const makes = await this.loadMakes(classMatch.value);
    if (!stillCurrent()) return;
    const makeMatch = slot.equipmentMake
      ? makes.find((m) => norm(m.value) === norm(slot.equipmentMake))
      : null;

    if (!makeMatch) {
      this.setActiveSelection(
        { selectedMake: null, equipmentMake: "", equipmentMakeDescription: "", selectedModel: null, equipmentModel: "", equipmentModelDescription: "" },
        { reconciling: false, catalogModels: [], catalogRate: null });
      return;
    }

    this.setActiveSelection({
      selectedMake: { value: makeMatch.value, label: makeMatch.label, description: makeMatch.description },
      equipmentMake: makeMatch.value,
      equipmentMakeDescription: makeMatch.description,
    });

    // loadModels resolves the rate itself when the make has no model codes at all.
    const models = await this.loadModels(classMatch.value, makeMatch.value);
    if (!stillCurrent()) return;

    if (models.length === 0) {
      this.setActiveSelection({ selectedModel: null, equipmentModel: "", equipmentModelDescription: "" }, { reconciling: false });
      return;
    }

    const modelMatch = slot.equipmentModel
      ? models.find((m) => norm(m.value) === norm(slot.equipmentModel))
      : null;

    if (!modelMatch) {
      this.setActiveSelection({ selectedModel: null, equipmentModel: "", equipmentModelDescription: "" }, { reconciling: false, catalogRate: null });
      return;
    }

    this.setActiveSelection(
      {
        selectedModel: { value: modelMatch.value, label: modelMatch.label, description: modelMatch.description },
        equipmentModel: modelMatch.value,
        equipmentModelDescription: modelMatch.description,
      },
      { reconciling: false });
    await this.loadRate(classMatch.value, makeMatch.value, modelMatch.value);
  };

  // ─── Change handlers ───────────────────────────────────────────────────────

  handleChange = (e) =>
    this.setState({
      formData: { ...this.state.formData, [e.target.name]: e.target.value },
    });

  handleChange_check = (e) =>
    this.setState({
      formData: { ...this.state.formData, [e.target.name]: e.target.checked },
    });

  // Manual mode's free-text Class/Make/Code and Rate/DelayFactor/OTFactor all
  // live in the active slot (manualEntry), not formData.
  handleChangeManualField = (e) =>
    this.setActiveSelection({ [e.target.name]: e.target.value });

  // Mode/year switches preserve every mode's Class/Make/Code/Rate — only the
  // catalog reference data (tied to type+year) is reset, then reconciled
  // against the newly-active slot.
  handleModeChange = (mode) => {
    this.setState(
      {
        entryMode: mode,
        search: { query: "", results: [], loading: false },
        catalogClasses: [], catalogMakes: [], catalogModels: [], catalogRate: null,
      },
      () => { if (mode !== MODE.MANUAL) this.reconcileCatalogSelection(); }
    );
  };

  handleCatalogYearChange = (selectedCatalogYear) => {
    this.setState(
      {
        selectedCatalogYear,
        search: { query: "", results: [], loading: false },
        catalogClasses: [], catalogMakes: [], catalogModels: [], catalogRate: null,
      },
      () => { if (this.state.entryMode !== MODE.MANUAL) this.reconcileCatalogSelection(); }
    );
  };

  // ─── Catalog search (Standard/Miscellaneous helper) ─────────────────────────

  handleSearchChange = (e) => {
    const query = e.target.value;
    this.setState((s) => ({ search: { ...s.search, query, results: [] } }));
    clearTimeout(this._searchTimer);
    const hasValidSegment = query.split("/").some((seg) => seg.trim().length >= 2);
    if (hasValidSegment) {
      this._searchTimer = setTimeout(() => this.loadSearch(query), 300);
    }
  };

  loadSearch = async (query) => {
    this.setState((s) => ({ search: { ...s.search, loading: true } }));
    try {
      const res = await axios.get(`${CATALOG_BASE}/Search`, {
        params: { query, equipmentType: this.equipmentType, reportDate: this.reportDateStr },
      });
      this.setState((s) => ({ search: { ...s.search, loading: false, results: res.data || [] } }));
    } catch {
      this.setState((s) => ({ search: { ...s.search, loading: false } }));
    }
  };

  applyCatalogMatch = (result) => {
    const classOption = {
      value: result.equipmentClass,
      label: `[ ${result.equipmentClass} ] ${result.equipmentClassDescription}`,
      description: result.equipmentClassDescription,
    };
    const makeOption = {
      value: result.equipmentMake,
      label: `[ ${result.equipmentMake} ] ${result.equipmentMakeDescription}`,
      description: result.equipmentMakeDescription,
    };
    const modelOption = {
      value: result.equipmentModel,
      label: `[ ${result.equipmentModel} ] ${result.equipmentModelDescription}`,
      description: result.equipmentModelDescription,
    };
    this.setActiveSelection(
      {
        selectedClass: classOption,
        equipmentClass: result.equipmentClass,
        equipmentClassDescription: result.equipmentClassDescription,
        selectedMake: makeOption,
        equipmentMake: result.equipmentMake,
        equipmentMakeDescription: result.equipmentMakeDescription,
        selectedModel: modelOption,
        equipmentModel: result.equipmentModel,
        equipmentModelDescription: result.equipmentModelDescription,
      },
      {
        search: { query: "", results: [], loading: false },
        catalogMakes: [],
        catalogModels: [],
        catalogRate: { rate: result.rate, otFactor: result.otFactor, delayFactor: result.delayFactor },
      }
    );
  };

  handleSearchSelect = (result) => this.applyCatalogMatch(result);

  // The browser popup can be flipped to the *other* catalog type without closing it
  // (see EquipmentCatalogBrowserModal), so a selected row may not match whatever mode
  // is currently active here — switch to the mode matching the row's own type first,
  // loading that mode's Class list so its dropdown stays usable, before writing the match
  // into that mode's slot (never the still-active one).
  handleCatalogBrowserSelect = (result) => {
    const resultMode = result.equipmentType === "M" ? MODE.MISC : MODE.STANDARD;
    if (resultMode !== this.state.entryMode) {
      this.setState(
        {
          entryMode: resultMode,
          search: { query: "", results: [], loading: false },
          catalogClasses: [], catalogMakes: [], catalogModels: [], catalogRate: null,
        },
        () => {
          this.loadClasses(this.equipmentType);
          this.applyCatalogMatch(result);
        }
      );
    } else {
      this.applyCatalogMatch(result);
    }
    this.setState({ showCatalogBrowser: false });
  };

  handleChange_class = (selectedOption) => {
    this.setActiveSelection(
      {
        selectedClass: selectedOption,
        equipmentClass: selectedOption.value,
        equipmentClassDescription: selectedOption.description,
        selectedMake: null, equipmentMake: "", equipmentMakeDescription: "",
        selectedModel: null, equipmentModel: "", equipmentModelDescription: "",
      },
      {
        catalogMakes: [], catalogModels: [], catalogRate: null,
        formData: { ...this.state.formData, description: "" },
      },
      () => this.loadMakes(selectedOption.value)
    );
  };

  handleChange_make = (selectedOption) => {
    const active = this.getActiveSelection();
    this.setActiveSelection(
      {
        selectedMake: selectedOption,
        equipmentMake: selectedOption.value,
        equipmentMakeDescription: selectedOption.description,
        selectedModel: null, equipmentModel: "", equipmentModelDescription: "",
      },
      {
        catalogModels: [], catalogRate: null,
        formData: { ...this.state.formData, description: "" },
      },
      () => this.loadModels(active.equipmentClass, selectedOption.value)
    );
  };

  handleChange_model = (selectedOption) => {
    const active = this.getActiveSelection();
    this.setActiveSelection(
      { selectedModel: selectedOption, equipmentModel: selectedOption.value, equipmentModelDescription: selectedOption.description },
      { catalogRate: null },
      () => this.loadRate(active.equipmentClass, active.equipmentMake, selectedOption.value)
    );
  };

  // ─── Validation ────────────────────────────────────────────────────────────

  formIsIncomplete = () => {
    const { formData, entryMode, catalogModels, loadingCatalog, reconciling } = this.state;
    const active = this.getActiveSelection();
    if (!formData.contractorId) return true;
    if (!formData.equipmentNumber.trim()) return true;
    if (entryMode === MODE.MANUAL) return !formData.description.trim();
    if (reconciling) return true;
    const noModelsAvailable = !loadingCatalog && !!active.equipmentMake && catalogModels.length === 0 && !active.selectedModel;
    return (
      !active.equipmentClass ||
      !active.equipmentMake ||
      (!active.equipmentModel && !noModelsAvailable)
    );
  };

  getDuplicateNumbers = () => {
    const { formData } = this.state;
    const { isNew, projEquipList } = this.props;
    if (!isNew || !formData.contractorId || !formData.equipmentNumber.trim()) return [];
    return formData.equipmentNumber.split(",")
      .map((n) => n.trim())
      .filter((n) => n && projEquipList.some(
        (e) => e.contractorId === formData.contractorId &&
               (e.number || "").toLowerCase() === n.toLowerCase()
      ));
  };

  // ─── Submit ────────────────────────────────────────────────────────────────

  handleSubmit = async (e) => {
    e.preventDefault();
    if (this.formIsIncomplete()) return;

    const { dispatch, projectId, toggleEditEquipModal, isNew } = this.props;
    const {
      formData, entryMode, impactData,
      originalNumber, originalClass, originalMake, originalModel,
    } = this.state;
    const isCatalog = entryMode !== MODE.MANUAL;
    const active = this.getActiveSelection();

    // Equipment (Number/Class/Make/Model) is a shared catalog record: editing it here
    // updates every project this contractor's equipment is used on. Fetch impact first
    // and show a confirmation for any identifying-field change, not just the number.
    if (!isNew && !impactData) {
      const identityChanged =
        formData.equipmentNumber.trim() !== originalNumber.trim() ||
        active.equipmentClass !== originalClass ||
        active.equipmentMake !== originalMake ||
        active.equipmentModel !== originalModel;
      if (identityChanged) {
        this.setState({ checkingImpact: true });
        try {
          const impact = await dispatch(
            equipmentActions.getNumberChangeImpact(formData.projectEquipmentId)
          );
          const hasImpact = impact.draftCount + impact.submittedCount + impact.approvedCount > 0;
          if (hasImpact) {
            this.setState({ impactData: impact, checkingImpact: false });
            return;
          }
        } catch {
          // Impact check failed — proceed with save anyway
        }
        this.setState({ checkingImpact: false });
      }
    }

    const payload = {
      projectId,
      contractorId: formData.contractorId,
      projectEquipmentId: formData.projectEquipmentId,
      equipmentNumber: formData.equipmentNumber.trim(),
      isRental: formData.isRental,
      isManualEntry: !isCatalog,
      equipmentType: isCatalog ? this.equipmentType : null,
      equipmentClass: (active.equipmentClass || "").trim() || null,
      equipmentMake: (active.equipmentMake || "").trim() || null,
      equipmentModel: (active.equipmentModel || "").trim() || null,
      equipmentClassDescription: isCatalog ? (active.equipmentClassDescription || null) : null,
      equipmentMakeDescription: isCatalog ? (active.equipmentMakeDescription || null) : null,
      equipmentModelDescription: isCatalog ? (active.equipmentModelDescription || null) : null,
      description: formData.description.trim(),
      rate: !isCatalog ? parseRate(active.rate) : null,
      delayFactor: !isCatalog ? parseRate(active.delayFactor) : null,
      otFactor: !isCatalog ? parseRate(active.otFactor) : null,
    };

    if (isNew) {
      await dispatch(equipmentActions.add(payload));
    } else {
      await dispatch(equipmentActions.update(payload));
    }
    toggleEditEquipModal();
  };

  handleConfirmImpact = () => {
    // impactData stays set so the next handleSubmit call skips the impact check
    this.handleSubmit({ preventDefault: () => {} });
  };

  handleCancelImpact = () => {
    this.setState({ impactData: null });
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  render() {
    const {
      formData,
      entryMode,
      selectedCatalogYear,
      catalogYearOptions,
      catalogClasses,
      catalogMakes,
      catalogModels,
      catalogRate,
      loadingCatalog,
      reconciling,
      search,
      impactData,
      checkingImpact,
      showCatalogBrowser,
    } = this.state;
    const { showEditEquipModal, toggleEditEquipModal, projectId, isNew } = this.props;
    const active = this.getActiveSelection();
    const isCatalog = entryMode !== MODE.MANUAL;
    const busy = loadingCatalog || reconciling;
    const noModelsAvailable = !busy && !!active.equipmentMake && catalogModels.length === 0 && !active.selectedModel;

    const duplicateNumbers = this.getDuplicateNumbers();

    const closeBtn = (
      <button className="close" onClick={toggleEditEquipModal}>
        <FontAwesomeIcon icon="times" className="fa-fw text-white" />
      </button>
    );

    return (
      <React.Fragment>
      <Modal
        isOpen={showEditEquipModal}
        toggle={toggleEditEquipModal}
        size="lg"
        fade={false}
      >
        <ModalHeader
          toggle={toggleEditEquipModal}
          className="bg-info text-white"
          close={closeBtn}
        >
          <FontAwesomeIcon icon={isNew ? "plus" : "edit"} className="fa-fw mr-2" />
          {isNew ? "Add Equipment" : "Edit Equipment"}
        </ModalHeader>
        <ModalBody>
          <form id="editEquipForm" onSubmit={this.handleSubmit} autoComplete="off">
            {/* Contractor */}
            <ContractorSelector
              projectId={projectId}
              contractorId={formData.contractorId}
              setContractorId={(contractorId) =>
                this.setState({ formData: { ...this.state.formData, contractorId } })
              }
            />

            {!!formData.contractorId && (
            <React.Fragment>
            {/* Entry mode toggle */}
            <div className="card mx-2 mt-2 bg-lightgray">
              <div className="card-body py-2">
                {/* Catalog Year (catalog modes only) */}
                {isCatalog && (
                  <div className="form-row align-items-center mt-1">
                    <label className="col-sm-3 col-form-label"><b>Rate Year:</b></label>
                    <div className="col-sm-7 my-auto d-flex align-items-center">
                      <div style={{ flex: "0 0 110px" }}>
                        <Select
                          value={selectedCatalogYear}
                          options={catalogYearOptions}
                          onChange={this.handleCatalogYearChange}
                          styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                          isSearchable={false}
                        />
                      </div>
                      <button
                          type="button"
                          className="btn btn-outline-secondary ml-2 bg-info text-white flex-fill"
                          onClick={() => this.setState({ showCatalogBrowser: true })}
                          disabled={!formData.contractorId}
                        >
                          <FontAwesomeIcon icon="book" className="fa-fw mr-1" />
                          Browse Rental Rate Book
                        </button>
                    </div>
                  </div>
                )}
                <div className="form-row mt-2">
                  <label className="col-sm-3 col-form-label"><b>Rate Type:</b></label>
                  <div className="col-sm-7 my-auto d-flex align-items-center">
                    <div className="btn-group btn-group-sm d-flex w-100" role="group">
                      {[
                        { key: MODE.STANDARD, label: "Standard" },
                        { key: MODE.MISC, label: "Miscellaneous" },
                        { key: MODE.MANUAL, label: "Manual Entry" },
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          className={`btn ${entryMode === key ? "btn-primary" : "btn-outline-secondary"}`}
                          style={{ flex: "1 1 0" }}
                          onClick={() => this.handleModeChange(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Common: Number */}
            <div className="card m-2">
              <div className="card-body">
                <div className="form-row form-group mb-3">
                  <label htmlFor="equipmentNumber" className="col-sm-3 col-form-label">
                    <b>Number:</b>*
                  </label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      name="equipmentNumber"
                      id="equipmentNumber"
                      className="form-control"
                      maxLength="20"
                      value={formData.equipmentNumber}
                      onChange={this.handleChange}
                      disabled={!formData.contractorId}
                    />
                    <small className="text-muted">If more than one, separate by comma.</small>
                    {duplicateNumbers.length > 0 && (
                      <div className="alert alert-warning py-1 px-2 mb-0 mt-1">
                        <FontAwesomeIcon icon="exclamation-triangle" className="fa-fw mr-1" />
                        <small>
                          Equipment number{duplicateNumbers.length > 1 ? "s" : ""}{" "}
                          <b>{duplicateNumbers.join(", ")}</b>{" "}
                          already exist{duplicateNumbers.length === 1 ? "s" : ""} for this contractor.
                        </small>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Search bar (catalog modes) ── */}
                {isCatalog && (
                  <div className="form-row form-group mb-2">
                    <label className="col-sm-3 col-form-label">Search ERRB:</label>
                    <div className="col-sm-9" style={{ position: "relative" }}>
                      <input
                        type="text"
                        className="form-control"
                        value={search.query}
                        onChange={this.handleSearchChange}
                        placeholder="Class / Make / Code  e.g. ROLLER / BOMAG / BW211"
                        disabled={!formData.contractorId}
                        autoComplete="off"
                      />
                      {search.loading && <small className="text-muted ml-1">Searching...</small>}
                      {search.results.length > 0 && (
                        <div
                          className="list-group shadow-sm"
                          style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999, maxHeight: 220, overflowY: "auto" }}
                        >
                          {search.results.map((r, i) => (
                            <button
                              key={i}
                              type="button"
                              className="list-group-item list-group-item-action py-1 px-2 text-left"
                              style={{ fontSize: "0.78rem" }}
                              onClick={() => this.handleSearchSelect(r)}
                            >
                              {r.equipmentClassDescription}{" "}
                              <b>{r.equipmentClass}</b>
                              {" / "}
                              {r.equipmentMakeDescription}{" "}
                              <b>{r.equipmentMake}</b>
                              {" / "}
                              {r.equipmentModelDescription}{" "}
                              <b>{r.equipmentModel}</b>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Catalog mode: cascading dropdowns, must match the catalog ── */}
                {isCatalog && (
                  <React.Fragment>
                    <div className="form-row form-group mb-2">
                      <label className="col-sm-3 col-form-label"><b>Class:</b>*</label>
                      <div className="col-sm-9">
                        <Select
                          value={active.selectedClass}
                          options={catalogClasses}
                          onChange={this.handleChange_class}
                          isDisabled={!formData.contractorId || busy}
                          isLoading={busy && catalogClasses.length === 0}
                          placeholder="(Select class)"
                          styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                        />
                      </div>
                    </div>
                    <div className="form-row form-group mb-2">
                      <label className="col-sm-3 col-form-label"><b>Make:</b>*</label>
                      <div className="col-sm-9">
                        <Select
                          value={active.selectedMake}
                          options={catalogMakes}
                          onChange={this.handleChange_make}
                          isDisabled={!active.equipmentClass || busy}
                          isLoading={busy && !!active.equipmentClass && catalogMakes.length === 0}
                          placeholder="(Select make)"
                          styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                        />
                      </div>
                    </div>
                    <div className="form-row form-group mb-2">
                      <label className="col-sm-3 col-form-label"><b>Model Code:</b>{!noModelsAvailable && "*"}</label>
                      <div className="col-sm-9">
                        {noModelsAvailable ? (
                          <p className="form-control-plaintext text-muted mb-0">
                            <small>(No code for this make)</small>
                          </p>
                        ) : (
                          <Select
                            value={active.selectedModel}
                            options={catalogModels}
                            onChange={this.handleChange_model}
                            isDisabled={!active.equipmentMake || busy}
                            isLoading={busy && !!active.equipmentMake && catalogModels.length === 0}
                            placeholder="(Select code)"
                            styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                          />
                        )}
                      </div>
                    </div>
                    {reconciling && (
                      <small className="text-muted d-block mb-2">
                        <FontAwesomeIcon icon="spinner" spin className="fa-fw mr-1" />
                        Checking catalog for the selected type/year...
                      </small>
                    )}
                  </React.Fragment>
                )}

                {/* ── Manual mode: free-form, unvalidated Class/Make/Code + static rate ── */}
                {entryMode === MODE.MANUAL && (
                  <React.Fragment>
                    <div className="form-row form-group mb-2">
                      <label className="col-sm-3 col-form-label">Class:</label>
                      <div className="col-sm-9">
                        <input
                          type="text"
                          name="equipmentClass"
                          className="form-control"
                          value={active.equipmentClass}
                          onChange={this.handleChangeManualField}
                          placeholder="(optional)"
                          disabled={!formData.contractorId}
                        />
                      </div>
                    </div>
                    <div className="form-row form-group mb-2">
                      <label className="col-sm-3 col-form-label">Make:</label>
                      <div className="col-sm-9">
                        <input
                          type="text"
                          name="equipmentMake"
                          className="form-control"
                          value={active.equipmentMake}
                          onChange={this.handleChangeManualField}
                          placeholder="(optional)"
                          disabled={!formData.contractorId}
                        />
                      </div>
                    </div>
                    <div className="form-row form-group mb-2">
                      <label className="col-sm-3 col-form-label">Model Code:</label>
                      <div className="col-sm-9">
                        <input
                          type="text"
                          name="equipmentModel"
                          className="form-control"
                          value={active.equipmentModel}
                          onChange={this.handleChangeManualField}
                          placeholder="(optional)"
                          disabled={!formData.contractorId}
                        />
                      </div>
                    </div>
                  </React.Fragment>
                )}

                <div className="form-row form-group mb-2">
                  {entryMode === MODE.MANUAL && (
                    <label className="col-sm-3 col-form-label">
                      <b>Description*:</b>
                    </label>
                  )}
                  {entryMode !== MODE.MANUAL && (
                    <label className="col-sm-3 col-form-label">
                      Description:
                    </label>
                  )}
                  <div className="col-sm-9">
                    <input
                      type="text"
                      name="description"
                      className="form-control"
                      value={formData.description}
                      onChange={this.handleChange}
                      placeholder="Enter Description (e.g. Ford F150)"
                      disabled={!formData.contractorId}
                    />
                  </div>
                </div>

                {isCatalog && catalogRate && (
                  <div className="alert alert-info py-2 mb-2">
                    <small>
                      <b>Rate ({selectedCatalogYear?.label || ""}):</b> ${catalogRate.rate}/hr &nbsp;|&nbsp;
                      OT Factor: {catalogRate.otFactor} &nbsp;|&nbsp;
                      Delay Factor: {catalogRate.delayFactor}
                    </small>
                  </div>
                )}

                {entryMode === MODE.MANUAL && (
                  <div className="form-row form-group mb-2">
                    <label className="col-sm-3 col-form-label">Rate:</label>
                    <div className="col-sm-9 form-row no-gutters">
                      <div className="col-4 pr-1">
                        <div className="input-group input-group-sm">
                          <div className="input-group-prepend">
                            <span className="input-group-text">$</span>
                          </div>
                          <input
                            type="number" step="0.01" name="rate" className="form-control"
                            value={active.rate} onChange={this.handleChangeManualField}
                            placeholder="Rate/hr" disabled={!formData.contractorId}
                          />
                        </div>
                      </div>
                      <div className="col-4 px-1">
                        <input
                          type="number" step="0.01" name="otFactor" className="form-control form-control-sm"
                          value={active.otFactor} onChange={this.handleChangeManualField}
                          placeholder="OT Factor" disabled={!formData.contractorId}
                        />
                      </div>
                      <div className="col-4 pl-1">
                        <input
                          type="number" step="0.01" name="delayFactor" className="form-control form-control-sm"
                          value={active.delayFactor} onChange={this.handleChangeManualField}
                          placeholder="Delay Factor" disabled={!formData.contractorId}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* IsRental */}
                <div className="form-row mb-0">
                  <div className="col-sm-3"></div>
                  <div className="col-sm-9">
                    <div className="custom-control-inline custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        name="isRental"
                        id="isRental"
                        className="custom-control-input my-auto ml-2"
                        disabled={!formData.contractorId}
                        checked={formData.isRental}
                        onChange={this.handleChange_check}
                      />
                      <label htmlFor="isRental" className="custom-control-label my-auto">
                        This is rental equipment
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </React.Fragment>
            )}
            <div className="ml-2">
              <small>* indicates required fields</small>
            </div>
          </form>
        </ModalBody>
        {impactData && (
          <div className="alert alert-warning mx-3 mb-0 mt-2 py-2">
            <FontAwesomeIcon icon="exclamation-triangle" className="fa-fw mr-2" />
            <b>This equipment record is shared across all projects it's used on. This change will affect daily reports:</b>
            <ul className="mb-1 mt-1">
              {(impactData.draftCount + impactData.submittedCount) > 0 && (
                <li>
                  <b>{impactData.draftCount + impactData.submittedCount}</b> draft/submitted
                  report(s) — changes will apply automatically.
                </li>
              )}
              {impactData.approvedCount > 0 && (
                <li>
                  <b>{impactData.approvedCount}</b> approved report(s) — an auto-approved
                  amendment will be created for each.
                </li>
              )}
            </ul>
            <div className="d-flex mt-1">
              <button
                className="btn btn-sm btn-warning mr-2"
                onClick={this.handleConfirmImpact}
              >
                Proceed
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={this.handleCancelImpact}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group mb-0">
            <input
              className="btn btn-primary"
              name="editEquipButton"
              type="submit"
              form="editEquipForm"
              onClick={this.handleSubmit}
              value={isNew ? "Add" : "Save"}
              disabled={this.formIsIncomplete() || checkingImpact || !!impactData}
            />{" "}
            {checkingImpact && (
              <FontAwesomeIcon icon="spinner" spin className="fa-fw ml-2 my-auto text-muted" />
            )}
            <button
              className="btn btn-secondary ml-2"
              onClick={toggleEditEquipModal}
            >
              Cancel
            </button>
          </div>
        </ModalFooter>
      </Modal>
      <EquipmentCatalogBrowserModal
        isOpen={showCatalogBrowser}
        equipmentType={this.equipmentType}
        reportDate={this.reportDateStr}
        catalogYearLabel={selectedCatalogYear ? selectedCatalogYear.label : ""}
        onClose={() => this.setState({ showCatalogBrowser: false })}
        onSelect={this.handleCatalogBrowserSelect}
      />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  equipmentDetails:
    (state.equipment.projEquipLists[ownProps.projectId] || []).find(
      (e) => e.equipmentId === ownProps.equipmentId
    ) || null,
  projEquipList: state.equipment.projEquipLists[ownProps.projectId] || [],
});

export default connect(mapStateToProps)(EditEquipmentModal);
